import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import classes from './ExpandableDescription.module.scss';

interface ExpandableDescriptionProps {
  text?: string | null;
  maxLines?: number;
  className?: string;
  textClassName?: string;
  color?: 'medium' | 'dark' | 'primary';
}

type Placement = 'bottom' | 'top';

const ExpandableDescription: React.FC<ExpandableDescriptionProps> = ({
  text,
  maxLines = 3,
  className,
  textClassName,
  color = 'medium',
}) => {
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const clampRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);

  const [overflows, setOverflows] = useState(false);
  const [placement, setPlacement] = useState<Placement>('bottom');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const el = clampRef.current;
    if (!el) return undefined;
    const check = () => setOverflows(el.scrollHeight - el.clientHeight > 1);
    check();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, maxLines]);

  const computePlacement = useCallback(() => {
    if (!wrapperRef.current) return;
    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const tipHeight = tipRef.current?.offsetHeight ?? 200;
    const spaceBelow = window.innerHeight - wrapperRect.bottom;
    const spaceAbove = wrapperRect.top;
    const needed = tipHeight + 8;
    if (spaceBelow < needed && spaceAbove > spaceBelow) {
      setPlacement('top');
    } else {
      setPlacement('bottom');
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (!overflows) return;
    computePlacement();
  }, [overflows, computePlacement]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!overflows) return;
      e.stopPropagation();
      e.preventDefault();
      computePlacement();
      setOpen((prev) => !prev);
    },
    [overflows, computePlacement]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!overflows) return;
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.stopPropagation();
      e.preventDefault();
      computePlacement();
      setOpen((prev) => !prev);
    },
    [overflows, computePlacement]
  );

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: Event) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler, true);
    document.addEventListener('touchstart', handler, true);
    window.addEventListener('scroll', () => setOpen(false), { once: true, capture: true });
    return () => {
      document.removeEventListener('mousedown', handler, true);
      document.removeEventListener('touchstart', handler, true);
    };
  }, [open]);

  useEffect(() => {
    if (!overflows && open) setOpen(false);
  }, [overflows, open]);

  if (!text) return null;

  const colorVar = `var(--ion-color-${color})`;

  return (
    <span
      ref={wrapperRef}
      className={classNames(classes.wrapper, className)}
      data-overflows={overflows ? 'true' : undefined}
      data-open={open ? 'true' : undefined}
      onMouseEnter={handleMouseEnter}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <span
        ref={clampRef}
        className={classNames(classes.clamp, textClassName)}
        style={{ WebkitLineClamp: maxLines, color: colorVar } as React.CSSProperties}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role={overflows ? 'button' : undefined}
        aria-expanded={overflows ? open : undefined}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={overflows ? 0 : undefined}
      >
        {text}
      </span>

      {overflows && (
        <span
          ref={tipRef}
          className={classNames(classes.hoverTip, textClassName)}
          style={{ color: colorVar }}
          data-placement={placement}
          aria-hidden
        >
          {text}
        </span>
      )}
    </span>
  );
};

export default ExpandableDescription;
